import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Save, UserPlus, Copy, Check, Mail, MessageSquare, Sparkles, AlertTriangle, Building2, Upload, X, Palette, Bell, Repeat } from "lucide-react";
import ThemePicker from "@/components/ThemePicker";
import CornerPicker from "@/components/CornerPicker";
import TexturePicker from "@/components/TexturePicker";
import PresetPicker from "@/components/PresetPicker";
import ReminderSettings from "@/components/ReminderSettings";
import FollowupSettings from "@/components/FollowupSettings";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [copiedToken, setCopiedToken] = useState("");
  const [integrations, setIntegrations] = useState(null);
  const [wsName, setWsName] = useState("");
  const [wsLogo, setWsLogo] = useState("");
  const fileInputRef = useRef(null);
  const isOwner = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    const [{ data: p }, { data: m }, { data: i }, { data: ig }, { data: ws }] = await Promise.all([
      api.get("/pipelines"),
      api.get("/team/members"),
      api.get("/team/invites"),
      api.get("/integrations/status"),
      api.get("/workspace/settings"),
    ]);
    setPipelines(p); setMembers(m); setInvites(i); setIntegrations(ig);
    setWsName(ws.name || "");
    setWsLogo(ws.logo_data_url || "");
  };
  useEffect(() => { load(); }, []);

  const saveWorkspace = async () => {
    try {
      await api.patch("/workspace/settings", { name: wsName, logo_data_url: wsLogo });
      toast.success("Workspace branding saved");
      await refreshMe();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo must be under 500 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setWsLogo(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setWsLogo("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const updateStages = (pid, text) => {
    setPipelines((prev) => prev.map((p) => p.id === pid ? { ...p, stages: text.split(",").map((s) => s.trim()).filter(Boolean) } : p));
  };
  const updateName = (pid, name) => {
    setPipelines((prev) => prev.map((p) => p.id === pid ? { ...p, name } : p));
  };
  const savePipeline = async (p) => {
    try {
      await api.put(`/pipelines/${p.id}`, { name: p.name, stages: p.stages });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removePipeline = async (pid) => {
    if (!confirm("Delete pipeline (and its deals)?")) return;
    try {
      await api.delete(`/pipelines/${pid}`);
      setPipelines((prev) => prev.filter((p) => p.id !== pid));
    } catch (e) { toast.error("Delete failed"); }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/team/invite", { email: inviteEmail });
      setInviteEmail("");
      if (data?.status === "added_existing") {
        // Existing user was added straight into the workspace as a member —
        // refresh the members list, no invite token needed.
        const { data: refreshed } = await api.get("/team/members");
        setMembers(refreshed || []);
        toast.success(`${data.email} is already on Peak — added to your workspace instantly`);
      } else {
        setInvites((prev) => [data, ...prev]);
        toast.success("Invite created — email sent · share the link below if needed");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const revokeInvite = async (id) => {
    if (!confirm("Revoke this invite?")) return;
    try {
      await api.delete(`/team/invites/${id}`);
      setInvites((prev) => prev.filter((i) => i.id !== id));
    } catch (e) { toast.error("Revoke failed"); }
  };
  const copyInviteLink = async (invite) => {
    const url = `${window.location.origin}/accept-invite/${invite.token}`;
    await navigator.clipboard.writeText(url);
    setCopiedToken(invite.token);
    setTimeout(() => setCopiedToken(""), 1500);
    toast.success("Invite link copied");
  };

  return (
    <div className="p-6 md:p-10" data-testid="settings-page">
      <div className="section-label">Configuration</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Settings</h1>

      {/* Appearance / Theme picker */}
      <div className="brutalist-card p-6 mt-8" data-testid="theme-settings">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-5 h-5" />
          <div className="section-label">Appearance</div>
        </div>
        <p className="text-sm text-[var(--peak-muted)] mb-4 max-w-2xl">
          Pick the theme you want to see across the whole app. Changes apply instantly and sync to every device you sign in on.
        </p>
        <ThemePicker />

        <div className="mt-8 pt-6 border-t border-[var(--peak-border)]">
          <div className="section-label mb-2">Corner style</div>
          <p className="text-sm text-[var(--peak-muted)] mb-4 max-w-2xl">
            Round out cards, inputs and buttons across the entire app, or keep the crisp editorial edges.
          </p>
          <CornerPicker />
        </div>

        <div className="mt-8 pt-6 border-t border-[var(--peak-border)]">
          <div className="section-label mb-2">Surface texture</div>
          <p className="text-sm text-[var(--peak-muted)] mb-4 max-w-2xl">
            Add a subtle texture behind the canvas — grain for editorial feel, dots for a blueprint vibe, or keep it flat.
          </p>
          <TexturePicker />
        </div>

        <div className="mt-8 pt-6 border-t border-[var(--peak-border)]">
          <div className="section-label mb-2">Your presets</div>
          <p className="text-sm text-[var(--peak-muted)] mb-4 max-w-2xl">
            Save the current combo (theme + corners + texture) as a named preset and switch between your favorites with one click.
          </p>
          <PresetPicker />
        </div>
      </div>

      {/* Appointment reminders */}
      <div className="brutalist-card p-6 mt-8" data-testid="reminder-card">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5" />
          <div className="section-label">Appointment reminders</div>
        </div>
        <p className="text-sm text-[var(--peak-muted)] mb-6 max-w-2xl">
          Auto-send an email + SMS reminder before every booking to lift show-up rates. Templates use variables like <code className="font-mono">{"{name}"}</code> and <code className="font-mono">{"{when}"}</code>.
        </p>
        <ReminderSettings />
      </div>

      {/* Follow-ups (weekly recap + note nudge) */}
      <div className="brutalist-card p-6 mt-8" data-testid="followup-card">
        <div className="flex items-center gap-2 mb-4">
          <Repeat className="w-5 h-5" />
          <div className="section-label">Follow-ups</div>
        </div>
        <p className="text-sm text-[var(--peak-muted)] mb-6 max-w-2xl">
          A weekly show-up recap and a nudge to log notes after every meeting — so nothing slips through the cracks.
        </p>
        <FollowupSettings />
      </div>

      {/* Workspace branding */}
      {isOwner && (
        <div className="brutalist-card p-6 mt-8" data-testid="workspace-branding">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5" />
            <div className="section-label">Workspace branding</div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="section-label mb-2 block">Workspace name</label>
              <input
                type="text"
                value={wsName}
                onChange={(e) => setWsName(e.target.value)}
                placeholder="e.g. Acme Sales"
                data-testid="workspace-name-input"
                className="w-full border border-[var(--peak-ink)] px-3 py-2 text-sm bg-white"
                maxLength={60}
              />
              <p className="text-xs text-[var(--peak-muted)] mt-2">Shown in the sidebar and outgoing emails.</p>
            </div>
            <div>
              <label className="section-label mb-2 block">Logo</label>
              <div className="flex items-center gap-3">
                {wsLogo ? (
                  <img src={wsLogo} alt="Logo preview" className="w-14 h-14 object-cover border border-[var(--peak-ink)]" data-testid="workspace-logo-preview" />
                ) : (
                  <div className="w-14 h-14 border border-dashed border-[var(--peak-ink)] flex items-center justify-center text-[var(--peak-muted)]">
                    <Building2 className="w-5 h-5" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="workspace-logo-input" />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="workspace-logo-upload-btn"
                    className="text-xs font-bold uppercase tracking-widest border border-[var(--peak-ink)] px-3 py-2 hover:bg-[var(--peak-ink)] hover:text-white transition-colors flex items-center gap-2"
                  >
                    <Upload className="w-3.5 h-3.5" /> Choose file
                  </button>
                  {wsLogo && (
                    <button
                      type="button"
                      onClick={clearLogo}
                      className="text-[10px] font-mono text-[var(--peak-muted)] hover:text-[var(--peak-accent)] flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-[var(--peak-muted)] mt-2">PNG/JPG under 500 KB. Square works best.</p>
            </div>
          </div>
          <button
            onClick={saveWorkspace}
            data-testid="workspace-save-btn"
            className="mt-6 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 flex items-center gap-2 transition-colors"
          >
            <Save className="w-3.5 h-3.5" /> Save branding
          </button>
        </div>
      )}

      {/* Integration status */}
      {integrations && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8" data-testid="integrations-status">
          <IntegrationCard label="Email" icon={Mail} status={integrations.email} detail={integrations.email.configured ? `Sender: ${integrations.email.sender}` : "Add RESEND_API_KEY"} testId="int-email" />
          <IntegrationCard label="SMS" icon={MessageSquare} status={integrations.sms} detail={integrations.sms.configured ? `From: ${integrations.sms.from_number}` : `Missing: ${(integrations.sms.missing || []).join(", ")}`} testId="int-sms" />
          <IntegrationCard label="AI" icon={Sparkles} status={integrations.ai} detail={integrations.ai.configured ? integrations.ai.provider : "Add EMERGENT_LLM_KEY"} testId="int-ai" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="brutalist-card p-6">
          <div className="section-label">Profile</div>
          <div className="mt-3 space-y-2">
            <div><div className="section-label">Name</div><div>{user?.name || "—"}</div></div>
            <div><div className="section-label">Email</div><div>{user?.email}</div></div>
            <div><div className="section-label">Role</div><div>{user?.role}</div></div>
            <div><div className="section-label">Booking link</div><div className="font-mono text-xs break-all">{typeof window !== "undefined" ? `${window.location.origin}/book/${encodeURIComponent(user?.email || "")}` : ""}</div></div>
          </div>
        </div>

        <div className="lg:col-span-2 brutalist-card p-6">
          <div className="section-label">Pipelines</div>
          <div className="mt-4 space-y-6">
            {pipelines.map((p) => (
              <div key={p.id} className="border border-[var(--peak-border)] p-4" data-testid={`settings-pipeline-${p.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="section-label">Name</label>
                    <input className="brutalist-input" value={p.name} onChange={(e) => updateName(p.id, e.target.value)} data-testid={`settings-pipe-name-${p.id}`} />
                  </div>
                  <div>
                    <label className="section-label">Stages (comma-separated)</label>
                    <input className="brutalist-input" value={p.stages.join(", ")} onChange={(e) => updateStages(p.id, e.target.value)} data-testid={`settings-pipe-stages-${p.id}`} />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => savePipeline(p)} data-testid={`settings-save-${p.id}`} className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-3 py-1.5 inline-flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                  <button onClick={() => removePipeline(p.id)} data-testid={`settings-delete-${p.id}`} className="border border-[var(--peak-ink)] hover:bg-[#E50000] hover:text-white hover:border-[#E50000] font-bold uppercase text-xs tracking-widest px-3 py-1.5 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="brutalist-card p-6">
          <div className="section-label">Team members</div>
          <div className="mt-3 space-y-2" data-testid="team-members-list">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-2 py-2 border-b border-[var(--peak-border)]" data-testid={`member-${m.id}`}>
                <div className="w-8 h-8 bg-[var(--peak-ink)] text-white flex items-center justify-center font-heading font-bold">
                  {(m.name || m.email).slice(0,1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{m.name || "—"}</div>
                  <div className="section-label truncate">{m.email}</div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] px-1.5 py-0.5">{m.role}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 brutalist-card p-6">
          <div className="section-label">Invite teammates</div>
          <form onSubmit={sendInvite} className="mt-3 flex gap-2 flex-wrap" data-testid="invite-form">
            <input required type="email" placeholder="teammate@yourdomain.com" className="brutalist-input flex-1 min-w-[220px]" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
            <button type="submit" data-testid="invite-submit-btn" className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-4 py-2 inline-flex items-center gap-1"><UserPlus className="w-4 h-4" /> Invite</button>
          </form>

          <div className="section-label mt-6 mb-2">Pending invites</div>
          <div className="space-y-2" data-testid="invites-list">
            {invites.length === 0 && <div className="text-sm text-[var(--peak-muted)]">No pending invites.</div>}
            {invites.map((i) => (
              <div key={i.id} className="border border-[var(--peak-border)] p-3 flex items-center gap-2 flex-wrap" data-testid={`invite-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{i.email}</div>
                  <div className="section-label">{i.accepted ? "Accepted" : "Pending"} · sent {new Date(i.created_at).toLocaleDateString()}</div>
                </div>
                {!i.accepted && (
                  <>
                    <button onClick={() => copyInviteLink(i)} data-testid={`invite-copy-${i.id}`} className="border border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase text-xs tracking-widest px-3 py-1.5 inline-flex items-center gap-1">
                      {copiedToken === i.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedToken === i.token ? "Copied" : "Copy link"}
                    </button>
                    <button onClick={() => revokeInvite(i.id)} data-testid={`invite-revoke-${i.id}`} className="hover:text-[#E50000]"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ label, icon: Icon, status, detail, testId }) {
  const ok = status?.configured;
  return (
    <div className={`brutalist-card p-4 border-l-4 ${ok ? "border-l-[#008A27]" : "border-l-[#FFB800]"}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="section-label">{label}</span>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${ok ? "bg-[#008A27] text-white" : "bg-[#FFB800] text-black"}`}>
          {ok ? "Live" : "Mocked"}
        </span>
      </div>
      <div className="mt-2 font-heading font-bold text-lg tracking-tight">{status?.provider}</div>
      <div className="section-label mt-1 flex items-start gap-1">
        {!ok && <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-[#FFB800]" />}
        <span className="normal-case tracking-normal text-[11px]">{detail}</span>
      </div>
    </div>
  );
}
